/**
 * TimelineFilterStatusSection Component
 *
 * Filter buttons for status values (completed, pending, sent, etc.)
 */

import React, { useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { STATUS_OPTIONS } from "../types/timeline-filters";
import type { TimelineFilters } from "../types/timeline-filters";

interface TimelineFilterStatusSectionProps {
  filters: TimelineFilters;
  onToggleStatus: (status: string) => void;
}

const TimelineFilterStatusSectionBase: React.FC<
  TimelineFilterStatusSectionProps
> = ({ filters, onToggleStatus }) => {
  const handleToggle = useCallback(
    (status: string) => {
      onToggleStatus(status);
    },
    [onToggleStatus]
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Label className="text-sm font-semibold text-gray-700">Status</Label>
        {filters.status?.length && (
          <Badge variant="secondary" className="text-xs">
            {filters.status.length} selected
          </Badge>
        )}
      </div>
      <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
        {STATUS_OPTIONS.map((status) => {
          const isSelected = filters.status?.includes(status.value);
          return (
            <Button
              key={status.value}
              variant={isSelected ? "default" : "outline"}
              size="sm"
              onClick={() => handleToggle(status.value)}
              className={`h-9 text-sm font-medium transition-all ${
                isSelected
                  ? "bg-green-600 text-white shadow-md transform scale-105"
                  : "hover:bg-card-50 hover:border-gray-300"
              }`}
            >
              <span className="truncate">{status.label}</span>
            </Button>
          );
        })}
      </div>
    </div>
  );
};

export const TimelineFilterStatusSection = React.memo(
  TimelineFilterStatusSectionBase
);
